import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DragDropContext } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import { Modal, Button, Card, Menu, Header } from 'semantic-ui-react';
import i18next from 'i18next';
import PrimaryCategories from './PrimaryCategories';
import Subcategories from './Subcategories';
import MenuActions from './MenuActions';
import ItemsBank from './MenuItemsBank';
import MenuCategoryEditor from './MenuCategoryEditor';
import MenuItemEditor from './MenuItemEditor';
import ImportMenu from './ImportMenu';
import MenuTranslations from './MenuTranslations';
import SideEditor from '../../general/SideEditor';
import { getMenu, getCurrentRestaurant, getCurrentChain } from '../../../selectors';
import { fetchMenu, setEditedEntity, saveMenuCategory } from '../../../actions';
import MenusPicker from './MenusPicker';

const isMobile = window.innerWidth < 768;

const getMenuActionsStyle = (isRtl = i18next.dir() === 'rtl') => ({
    position: 'absolute',
    right: isRtl ? 280 : undefined,
    left: !isRtl ? 280 : undefined,
    bottom: isMobile ? 85 : 15,
    display: 'flex',
    flexDirection: 'column',
    // alignItems: isRtl ? 'flex-start' : 'flex-end'
});

class MenuEditor extends Component {
    state = { menuMode: isMobile ? 'items' : 'categories', editMode: 'master', showModal: false, menuId: null }

    constructor(props) {
        super(props);
        this.onDragEnd = this.onDragEnd.bind(this);
    }

    componentDidMount() {
        if (!this.state.menuId) {
            if (this.props.restaurant) {
                this.setState({ ...this.state, menuId: this.props.restaurant.menuId });
            } else if (!this.props.chain) {
                return this.props.history.push('/');
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.menu.info.isLinkedToMany !== this.props.menu.info.isLinkedToMany && this.props.menu.info.isLinkedToMany) {
            this.setState({ showModal: true });
        }

        if (!this.props.menu.info.id && this.state.menuId) {
            this.fetchMenu();
        }
    }

    getBranchIdForOverride = () => {
        return this.state.editMode === 'local' && this.props.restaurant ? this.props.restaurant.id : null;
    }

    fetchMenu = () => {
        this.props.fetchMenu(this.state.menuId, this.getBranchIdForOverride());
    }

    changeEditMode = editMode => {
        if (this.state.editMode === editMode) {
            return this.setState({ showModal: false });
        }

        this.setState({ editMode, showModal: false }, this.fetchMenu);
    }

    updateSortOrder = (updates) => {
        const { menuId } = this.state;
        const branchId = this.getBranchIdForOverride();

        this.props.saveMenuCategory(menuId, branchId, updates, true);
        this.props.saveMenuCategory(menuId, branchId, updates);
    }

    onDragEnd(result) {
        const { destination, source, draggableId, type } = result;

        if (!result.destination) {
            return;
        }

        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const { menuId } = this.state;

        if (type.startsWith('categories')) {
            const primaryCategoryId = destination.droppableId.split('-')[0];
            // const categoryId = draggableId.split('-')[1];

            let categories = [];

            if (primaryCategoryId === 'primary') {
                categories = Array.from(this.props.menu.primaryCategories);
            } else {
                categories = Array.from(Object.values(this.props.menu.allCategoriesDict).filter(c => c.primaryParentId === primaryCategoryId)).sort((a, b) => a.sortIndex - b.sortIndex);
            }

            const category = categories.splice(source.index, 1)[0];
            categories.splice(destination.index, 0, category);

            const updatedCategories = categories
                .map((cat, index) => ({ id: cat.id, sortIndex: index + 1 }))
                .filter((cat, index) => cat.sortIndex !== categories[index].sortIndex);

            updatedCategories.forEach(this.updateSortOrder);
        }

        if (type === 'subcategories') {
            const categoryId = destination.droppableId.split('-')[1];
            const subcategoryId = draggableId.split('-')[1];

            const category = this.props.menu.allCategoriesDict[categoryId];
            const newSubcategoriesIds = Array.from(category.subcategoriesIds);

            newSubcategoriesIds.splice(source.index, 1);
            newSubcategoriesIds.splice(destination.index, 0, subcategoryId);

            const updatedCategory = { id: categoryId, subcategoriesIds: newSubcategoriesIds };

            this.updateSortOrder(updatedCategory);
        }

        if (type === 'items') {
            const categoryId = destination.droppableId.split('-')[0];

            const category = this.props.menu.allCategoriesDict[categoryId];
            const itemsInstances = Array.from(category.itemsInstances);

            const item = itemsInstances.splice(source.index, 1)[0];
            itemsInstances.splice(destination.index, 0, item);

            const itemsInstancesToUpdate = itemsInstances
                .map((item, index) => ({ id: item.id, sortIndex: index + 1 }))
                .filter((item, index) => item.sortIndex !== itemsInstances[index].sortIndex);

            const branchId = this.getBranchIdForOverride();
            this.props.saveMenuCategory(menuId, branchId, { id: categoryId, itemsInstances }, true);
            this.props.saveMenuCategory(menuId, branchId, { id: categoryId, itemsInstancesToUpdate });
        }
    }

    scrollToSubcategory = (catId) => {
        const element = document.getElementById(`category:${catId}`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderModal() {
        return (
            <Modal
                open={this.state.showModal}
                onClose={() => this.setState({ showModal: this.props.menu.info.linkedBranches.length <= 1 })}
                style={{ direction: i18next.dir() }}
                size={'tiny'}
            >
                <Modal.Header as='h2' style={{ textAlign: 'center' }}>
                    {i18next.t('menu.editMode.modal.header')}
                </Modal.Header>
                <Modal.Content style={{ textAlign: 'center' }}>
                    <p>{i18next.t('menu.editMode.modal.content')}</p>
                    <p><b>{this.props.menu.info.linkedBranches.join(', ')}</b></p>
                    <p>{i18next.t('menu.editMode.modal.action')}</p>
                </Modal.Content>
                <Modal.Actions style={{ padding: 0 }}>
                    <Menu widths={2}>
                        <Menu.Item
                            active={this.state.editMode === 'local'}
                            onClick={() => this.changeEditMode('local')}
                        >
                            <div style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <Header as='h3'>{i18next.t(`menu.editMode.mode.local.title`)}</Header>
                                <p>{i18next.t(`menu.editMode.mode.local.hint`, { name: this.props.restaurant.name })}</p>
                            </div>
                        </Menu.Item>
                        <Menu.Item
                            active={this.state.editMode === 'master'}
                            disabled={this.props.menu.info.isLinkedToMany && this.props.menu.info.linkedBranches.length <= 1}
                            onClick={() => this.changeEditMode('master')}
                        >
                            <div style={{ paddingLeft: 5, paddingRight: 5 }}>
                                <Header as='h3'>{i18next.t(`menu.editMode.mode.master.title`)}</Header>
                                <p>{i18next.t(`menu.editMode.mode.master.hint`)}</p>
                            </div>
                        </Menu.Item>
                    </Menu>
                </Modal.Actions>
            </Modal>
        );
    }

    render() {
        if (!this.state.menuId) {
            if (this.props.restaurant) {
                return <ImportMenu restaurantId={this.props.restaurant.id} />;
            } else {
                return (
                    <MenusPicker
                        open={!this.state.menuId}
                        handleMenuPicked={(menuId) => this.setState({ ...this.state, menuId })}
                    />);
            }
        }

        return (
            <div style={{ height: '100%', width: '100%', display: 'flex' }}>
                <SideEditor
                    title={i18next.t('editCategory')}
                    visible={this.props.editedType === 'menuCategory'}
                    handleClose={() => this.props.setEditedEntity(null)}
                >
                    <MenuCategoryEditor branchIdForOverride={this.getBranchIdForOverride()} />
                </SideEditor>
                <SideEditor
                    title={i18next.t('editItem')}
                    visible={this.props.editedType === 'menuItem'}
                    handleClose={() => this.props.setEditedEntity(null)}
                >
                    <MenuItemEditor branchIdForOverride={this.getBranchIdForOverride()} />
                </SideEditor>
                {this.state.menuMode === 'translations' ?
                    <MenuTranslations menuId={this.state.menuId} />
                    :
                    <DragDropContext onDragEnd={this.onDragEnd}>
                        {this.state.menuMode === 'categories' &&
                            <PrimaryCategories
                                menuId={this.state.menuId}
                                categories={this.props.menu.primaryCategories}
                                handleSubcategoryClick={this.scrollToSubcategory}
                                branchIdForOverride={this.getBranchIdForOverride()}
                                
                                
                            />
                        }
                        {this.state.menuMode === 'items' &&
                            <ItemsBank
                                menuId={this.state.menuId}
                                handleSubcategoryClick={this.scrollToSubcategory}
                                preventItemsDeletion={!!this.getBranchIdForOverride()}
                                
                                branchIdForOverride={this.getBranchIdForOverride()}
                            />
                        }

                        <Subcategories
                            menuId={this.state.menuId}
                            categories={this.props.menu.subcategories}
                            handleSubcategoryClick={this.scrollToSubcategory}
                            branchIdForOverride={this.getBranchIdForOverride()}
                            
                        />
                    </DragDropContext>
                }
                <div style={getMenuActionsStyle()}>
                    {this.props.enableLocalEdit &&
                        <Card style={{ margin: 0 }}>
                            <Card.Content style={{ padding: 5, display: 'flex', alignItems: 'center' }}>
                                <Card.Meta style={{ flex: 1 }}>
                                    <span>{i18next.t('menu.editMode.status')}<b>{i18next.t(`menu.editMode.mode.${this.state.editMode}.title`)}</b></span>
                                </Card.Meta>
                                <Button basic size="mini" onClick={() => this.setState({ showModal: true })}>
                                    {i18next.t('menu.editMode.change').toUpperCase()}
                                </Button>
                            </Card.Content>
                        </Card>
                    }
                    <MenuActions
                        mode={this.state.menuMode}
                        toggleMode={menuMode => this.setState({ menuMode })}
                        onRefresh={this.fetchMenu}
                        menuId={this.state.menuId}
                        editInfo={this.props.menu.info}
                        
                    />
                </div>
                {this.props.enableLocalEdit && this.renderModal()}
            </div>
        );
    }
}

MenuEditor.propTypes = {
    restaurant: PropTypes.object,
    chain: PropTypes.object,
    history: PropTypes.object,
    menuId: PropTypes.any,
    menu: PropTypes.object,
    enableLocalEdit: PropTypes.bool,
    editedType: PropTypes.string,
    setEditedEntity: PropTypes.func,
    saveMenuCategory: PropTypes.func,
    fetchMenu: PropTypes.func
    
};

function mapStateToProps(state, ownProps) {
    const restaurant = getCurrentRestaurant(state);
    const chain = getCurrentChain(state);

    const { type: editedType } = state.editor;

    const menu = getMenu(state);

    const enableLocalEdit = !!restaurant;// && menu.info.isLinkedToMany;

    return { restaurant, editedType, menu, enableLocalEdit, chain };
}

export default connect(mapStateToProps, { fetchMenu, setEditedEntity, saveMenuCategory })(MenuEditor);
